/**
 * Subscription 360 and Order 360 — two descriptors on the one Entity 360 page.
 *
 * Everything generic comes free: the activity timeline, the audit trail, documents, approvals, exceptions and
 * integration provenance. What is written here is what is particular to a standing agreement and to a day's
 * demand — and, in both, an honest statement of what a later epic owns.
 *
 * NO MONEY APPEARS IN EITHER. There is no price master and whether the ERP owns one is CD-15, so a line shows
 * what the customer receives and what it weighs, and nothing about what it costs.
 */
import { Link } from 'react-router-dom';
import { Ban, CheckCircle2, CircleDashed, Hammer, PlayCircle, Repeat, StopCircle, Truck } from 'lucide-react';
import { rpc } from '../../lib/supabase';
import { Fact, FactGrid, Panel, PanelHeader, SectionLabel, StatusBadge, Unknown, cx } from '../../ui/primitives';
import { ProvenanceSection } from '../../entity360/sections';
import type { Entity360Descriptor } from '../../entity360/types';
import { date as fmtDate } from '../../ui/format';

const kg = (g: number | null | undefined) => (g === null || g === undefined ? null : `${(g / 1000).toLocaleString(undefined, { maximumFractionDigits: 3 })} kg`);

interface Line {
  variant_code: string; variant_name: string; quantity_units: number;
  container_volume_ml: number | null; fill_volume_ml: number | null;
  fill_mass_g?: number | null; grams_per_delivery?: number | null; quantity_g?: number | null;
  returnable?: boolean;
}
interface NotBuilt { tab?: string; epic?: string; detail?: string }

export interface Subscription360 {
  entity_type: string; id: string; code: string; status: string; schedule_pattern: string;
  start_date: string; end_date: string | null; paused_from: string | null; cancelled_on: string | null;
  note: string | null; data_origin: string; created_at: string; updated_at: string;
  customer: { id: string; code: string; name: string; status: string };
  customer_location: { id: string; code: string; label: string | null; area: string | null };
  route: { id: string; code: string | null } | null;
  lines: Line[];
  units_per_delivery: number; grams_per_delivery: number;
  changes: { id: string; change_type: string; effective_from: string; channel: string; reason: string | null;
             previous_state: unknown; new_state: unknown; created_at: string }[];
  counts: { lines: number; changes: number; orders: number | null; events: number };
  not_built: string[];
}

export interface Order360 {
  entity_type: string; id: string; code: string; status: string; source: string; scheduled_date: string;
  cancelled_on: string | null; cancel_reason: string | null; note: string | null;
  data_origin: string; created_at: string; updated_at: string;
  customer: { id: string; code: string; name: string; status: string };
  customer_location: { id: string; code: string; label: string | null; area: string | null; has_geo_pin: boolean };
  route: { id: string; code: string | null } | null;
  subscription: { id: string; code: string; status: string; schedule_pattern: string } | null;
  lines: Line[];
  units: number; grams: number | null;
  finished_goods_available: { variant_code: string; units_in_stock: number }[];
  counts: { lines: number; events: number };
  not_built: string[];
}

const SUB_TONE: Record<string, 'accepted' | 'queued' | 'neutral' | 'info'> = {
  ACTIVE: 'accepted', DRAFT: 'info', PAUSED: 'queued', CANCELLED: 'neutral', EXPIRED: 'neutral',
};
const SUB_ICON: Record<string, typeof Repeat> = {
  ACTIVE: PlayCircle, DRAFT: CircleDashed, PAUSED: StopCircle, CANCELLED: Ban, EXPIRED: CircleDashed,
};

function Lines({ lines }: { lines: Line[] }) {
  if (lines.length === 0) return <div className="px-4 py-6 text-body text-ink-muted">No lines.</div>;
  return (
    <table className="w-full text-body">
      <thead>
        <tr className="border-b border-line text-left">
          <th className="px-4 py-2 label-text">Variant</th>
          <th className="px-4 py-2 label-text">Container</th>
          <th className="px-4 py-2 label-text">Filled to</th>
          <th className="px-4 py-2 label-text text-right">Units</th>
          <th className="px-4 py-2 label-text text-right">Milk</th>
        </tr>
      </thead>
      <tbody>
        {lines.map((l) => (
          <tr key={l.variant_code} className="border-b border-line last:border-b-0">
            <td className="px-4 py-2"><span className="code-text">{l.variant_code}</span> <span className="text-ink-muted">{l.variant_name}</span></td>
            {/* The container and the fill are two different facts, and stay two columns all the way here. */}
            <td className="px-4 py-2 tabular text-ink-muted">{l.container_volume_ml ? `${l.container_volume_ml} ml` : <Unknown />}</td>
            <td className="px-4 py-2 tabular text-ink-muted">{l.fill_volume_ml ? `${l.fill_volume_ml} ml` : <Unknown />}</td>
            <td className="px-4 py-2 tabular text-right">{l.quantity_units}</td>
            <td className="px-4 py-2 tabular text-right">{kg(l.grams_per_delivery ?? l.quantity_g) ?? <Unknown title="This variant has no fill mass" />}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function NotBuiltNote({ items }: { items: (string | NotBuilt)[] }) {
  if (!items || items.length === 0) return null;
  return (
    <div className="border-t border-line px-4 py-3">
      <SectionLabel>Not built yet</SectionLabel>
      <p className="mt-1 flex items-start gap-2 text-small text-ink-muted">
        <Hammer size={13} className="mt-0.5 shrink-0 text-ink-faint" aria-hidden />
        {/* Named, not shown as empty tables: an empty Payments table reads as "nothing is owed". */}
        <span>{items.map((i) => (typeof i === 'string' ? i : `${i.tab} (${i.epic})`)).join(' · ')}.</span>
      </p>
    </div>
  );
}

function SubscriptionOverview({ entity }: { entity: Subscription360 }) {
  return (
    <div>
      <FactGrid columns={3}>
        <Fact label="Customer">
          <Link to={`/customers/${entity.customer.id}`} className="hover:text-brand">
            <span className="code-text">{entity.customer.code}</span> {entity.customer.name}
          </Link>
        </Fact>
        <Fact label="Door" mono>{entity.customer_location.code}{entity.customer_location.label ? ` · ${entity.customer_location.label}` : ''}</Fact>
        <Fact label="Round" mono>{entity.route?.code ?? <Unknown title="Not on a round" />}</Fact>
        <Fact label="How often">{entity.schedule_pattern.toLowerCase()}</Fact>
        <Fact label="From">{fmtDate(entity.start_date)}</Fact>
        <Fact label="Until">{entity.end_date ? fmtDate(entity.end_date) : <Unknown title="No end date" />}</Fact>
        <Fact label="Units a delivery">{entity.units_per_delivery}</Fact>
        <Fact label="A delivery weighs">{kg(entity.grams_per_delivery)}</Fact>
        <Fact label="Paused from">{entity.paused_from ? fmtDate(entity.paused_from) : <Unknown title="Not paused" />}</Fact>
      </FactGrid>
      <div className="border-t border-line"><Lines lines={entity.lines} /></div>
      <NotBuiltNote items={entity.not_built} />
    </div>
  );
}

function Amendments({ entity }: { entity: Subscription360 }) {
  if (entity.changes.length === 0) return <div className="px-4 py-6 text-body text-ink-muted">No amendment yet.</div>;
  return (
    <ul className="divide-y divide-line">
      {entity.changes.map((c) => (
        <li key={c.id} className="px-4 py-2.5">
          <p className="text-body">
            {c.change_type.replace(/_/g, ' ').toLowerCase()}
            <span className="ml-2 text-small text-ink-muted">from {fmtDate(c.effective_from)}</span>
            {/* CD-25: which mechanism asked is recorded, and the ERP ranks none of them above another. */}
            <span className="ml-2 rounded-[var(--radius-badge)] border border-line px-1 text-[11px] uppercase tracking-wide text-ink-faint">
              {c.channel.replace(/_/g, ' ').toLowerCase()}
            </span>
          </p>
          {c.reason ? <p className="text-small text-ink-muted">{c.reason}</p> : null}
        </li>
      ))}
    </ul>
  );
}

export const subscriptionDescriptor: Entity360Descriptor<Subscription360> = {
  entityType: 'sales.subscriptions',
  kind: 'subscriptions',
  registerPath: '/customers/subscriptions',
  registerLabel: 'Subscriptions',

  load: (id) => rpc<Subscription360>('subscription_360', { p_id: id }),
  title: (e) => `${e.customer.name} · ${e.customer_location.label ?? e.customer_location.code}`,
  code: (e) => e.code,
  status: (e) => {
    const Icon = SUB_ICON[e.status] ?? Repeat;
    return <StatusBadge tone={SUB_TONE[e.status] ?? 'neutral'} icon={Icon}>{e.status.toLowerCase()}</StatusBadge>;
  },

  summary: (e) => (
    <dl className="flex flex-wrap gap-x-8 gap-y-1">
      <div><dt className="label-text">Units a delivery</dt><dd className="tabular">{e.units_per_delivery}</dd></div>
      <div><dt className="label-text">Weighs</dt><dd className="tabular">{kg(e.grams_per_delivery)}</dd></div>
      <div><dt className="label-text">How often</dt><dd>{e.schedule_pattern.toLowerCase()}</dd></div>
      <div><dt className="label-text">Orders</dt><dd className="tabular">{e.counts.orders ?? '—'}</dd></div>
      <div><dt className="label-text">Amendments</dt><dd className="tabular">{e.counts.changes}</dd></div>
    </dl>
  ),

  rail: (entity, context) => (
    <div className="flex flex-col gap-4">
      <Panel>
        <PanelHeader title="Where it came from" />
        <ProvenanceSection {...context} />
      </Panel>
      <Panel>
        <PanelHeader title="Customer" />
        <div className="px-4 py-3 text-body">
          <Link to={`/customers/${entity.customer.id}`} className="hover:text-brand">
            <span className="code-text">{entity.customer.code}</span>
            <span className="block text-ink-muted">{entity.customer.name}</span>
          </Link>
        </div>
      </Panel>
    </div>
  ),

  tabs: [
    { id: 'overview', label: 'Overview', section: 'custom', render: (e) => <SubscriptionOverview entity={e} /> },
    { id: 'lines', label: 'What it is for', section: 'custom', count: (e) => e.counts.lines, render: (e) => <Lines lines={e.lines} /> },
    { id: 'amendments', label: 'Amendments', section: 'custom', count: (e) => e.counts.changes, render: (e) => <Amendments entity={e} /> },
    { id: 'activity', label: 'Activity', section: 'activity' },
    { id: 'approvals', label: 'Approvals', section: 'approvals' },
    { id: 'audit', label: 'Audit', section: 'audit', permission: 'audit.view' },
  ],
};

function OrderOverview({ entity }: { entity: Order360 }) {
  return (
    <div>
      <FactGrid columns={3}>
        <Fact label="For">{fmtDate(entity.scheduled_date)}</Fact>
        <Fact label="Customer">
          <Link to={`/customers/${entity.customer.id}`} className="hover:text-brand">
            <span className="code-text">{entity.customer.code}</span> {entity.customer.name}
          </Link>
        </Fact>
        <Fact label="Door" mono>{entity.customer_location.code}{entity.customer_location.label ? ` · ${entity.customer_location.label}` : ''}</Fact>
        <Fact label="From">{entity.source.replace(/_/g, ' ').toLowerCase()}</Fact>
        <Fact label="Agreement" mono>
          {entity.subscription
            ? <Link to={`/customers/subscriptions/${entity.subscription.id}`} className="hover:text-brand">{entity.subscription.code}</Link>
            : <Unknown title="Entered by hand, not from a standing agreement" />}
        </Fact>
        <Fact label="Round" mono>{entity.route?.code ?? <Unknown title="Not on a round" />}</Fact>
        <Fact label="Units">{entity.units}</Fact>
        <Fact label="Weighs">{kg(entity.grams) ?? <Unknown title="A variant on this order has no fill mass" />}</Fact>
        <Fact label="Map pin">{entity.customer_location.has_geo_pin ? 'set' : <Unknown title="No map pin on this door" />}</Fact>
      </FactGrid>
      <div className="border-t border-line"><Lines lines={entity.lines} /></div>
      {entity.cancel_reason ? (
        <div className="border-t border-line px-4 py-3">
          <SectionLabel>Cancelled because</SectionLabel>
          <p className="mt-1 text-small text-ink-muted">{entity.cancel_reason}</p>
        </div>
      ) : null}
      <NotBuiltNote items={entity.not_built} />
    </div>
  );
}

/** Where the commercial chain meets the operational one. Both numbers are reads; neither side invents the other. */
function AgainstStock({ entity }: { entity: Order360 }) {
  return (
    <div>
      <p className="border-b border-line bg-surface px-4 py-2 text-small text-ink-muted">
        What this order asks for, beside what the plant has of those variants right now. Both are read from their own
        ledgers: the order does not reserve stock, and stock does not change because an order exists.
      </p>
      <table className="w-full text-body">
        <thead>
          <tr className="border-b border-line text-left">
            <th className="px-4 py-2 label-text">Variant</th>
            <th className="px-4 py-2 label-text text-right">Asked for</th>
            <th className="px-4 py-2 label-text text-right">In finished goods</th>
          </tr>
        </thead>
        <tbody>
          {entity.lines.map((l) => {
            const have = entity.finished_goods_available.find((f) => f.variant_code === l.variant_code)?.units_in_stock ?? 0;
            return (
              <tr key={l.variant_code} className="border-b border-line last:border-b-0">
                <td className="px-4 py-2 code-text">{l.variant_code}</td>
                <td className="px-4 py-2 tabular text-right">{l.quantity_units}</td>
                <td className={cx('px-4 py-2 tabular text-right', have < l.quantity_units && 'text-queued')}>{have}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export const orderDescriptor: Entity360Descriptor<Order360> = {
  entityType: 'sales.customer_orders',
  kind: 'customer-orders',
  registerPath: '/customers/orders',
  registerLabel: 'Orders',

  load: (id) => rpc<Order360>('order_360', { p_id: id }),
  title: (e) => `${e.customer.name} · ${fmtDate(e.scheduled_date)}`,
  code: (e) => e.code,
  status: (e) => (
    <StatusBadge
      tone={e.status === 'CONFIRMED' ? 'accepted' : e.status === 'PLANNED' ? 'info' : 'neutral'}
      icon={e.status === 'CONFIRMED' ? CheckCircle2 : e.status === 'PLANNED' ? CircleDashed : Ban}
    >
      {e.status.toLowerCase()}
    </StatusBadge>
  ),

  summary: (e) => (
    <dl className="flex flex-wrap gap-x-8 gap-y-1">
      <div><dt className="label-text">For</dt><dd>{fmtDate(e.scheduled_date)}</dd></div>
      <div><dt className="label-text">Units</dt><dd className="tabular">{e.units}</dd></div>
      <div><dt className="label-text">Weighs</dt><dd className="tabular">{kg(e.grams) ?? '—'}</dd></div>
      <div><dt className="label-text">Round</dt><dd className="code-text">{e.route?.code ?? '—'}</dd></div>
      <div><dt className="label-text">From</dt><dd>{e.source.toLowerCase()}</dd></div>
    </dl>
  ),

  rail: (_entity, context) => (
    <div className="flex flex-col gap-4">
      <Panel>
        <PanelHeader title="Where it came from" />
        <ProvenanceSection {...context} />
      </Panel>
      <Panel>
        <PanelHeader title="Next" />
        <p className="flex items-start gap-2 px-4 py-3 text-small text-ink-muted">
          <Truck size={13} className="mt-0.5 shrink-0 text-ink-faint" aria-hidden />
          <span>
            A confirmed order is handed to delivery with its customer, door, variants, quantities and date. What was
            actually handed over, the door verification and the containers returned are delivery&rsquo;s to record, not this
            order&rsquo;s.
          </span>
        </p>
      </Panel>
    </div>
  ),

  tabs: [
    { id: 'overview', label: 'Overview', section: 'custom', render: (e) => <OrderOverview entity={e} /> },
    { id: 'lines', label: 'What it is for', section: 'custom', count: (e) => e.counts.lines, render: (e) => <Lines lines={e.lines} /> },
    { id: 'stock', label: 'Against stock', section: 'custom', permission: 'inventory.view', render: (e) => <AgainstStock entity={e} /> },
    { id: 'activity', label: 'Activity', section: 'activity' },
    { id: 'audit', label: 'Audit', section: 'audit', permission: 'audit.view' },
  ],
};
